import React, { useState, useEffect, useCallback } from 'react';
import {
  getEvents,
  getEventMedia, getPendingMediaModeration,
  uploadEventMaterial, moderateMedia,
  updateMediaPrivacy, deleteMedia,
  sendMaterialToRegistrants,
  fmtDateTime,
} from '../../../api/eventsApi';

const TABS = [
  { key: 'materials',    label: 'Event Materials' },
  { key: 'moderation',   label: 'Photo Moderation' },
  { key: 'upload',       label: 'Upload Material' },
];

const VISIBILITY_LABELS = {
  CONFIRMED_ONLY: 'Confirmed Only',
  ALL_MEMBERS:    'All Members',
  ADMIN_ONLY:     'Admin Only',
};

const TYPE_LABELS = {
  PRE_EVENT_MATERIAL: 'Pre-Event Material',
  POST_EVENT_PHOTO:   'Post-Event Photo',
};

function TabBar({ activeTab, onSelect }) {
  return (
    <div className="bb-prog-tabs">
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function EventSelector({ events, value, onChange }) {
  return (
    <div className="bb-form-group" style={{ maxWidth: 460, marginTop: 24, marginBottom: 20 }}>
      <label style={{ paddingLeft: '3ch' }}>Select Event</label>
      <select className="bb-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">-- Select an event --</option>
        {events.map(evt => (
          <option key={evt.id} value={evt.id}>{evt.title} ({fmtDateTime(evt.start_datetime)})</option>
        ))}
      </select>
    </div>
  );
}

export default function EventContentMedia({ orgId, adminId, adminName }) {
  const [activeTab, setActiveTab] = useState('materials');

  const [allEvents, setAllEvents]     = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Materials tab
  const [matEventId, setMatEventId]   = useState('');
  const [materials, setMaterials]     = useState([]);
  const [matLoading, setMatLoading]   = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState(null);
  const [showVisModal, setShowVisModal]         = useState(false);
  const [visTarget, setVisTarget]               = useState(null);
  const [newVisibility, setNewVisibility]       = useState('CONFIRMED_ONLY');

  // Send-to-registrants modal
  const [showSendModal, setShowSendModal]       = useState(false);
  const [sendTarget, setSendTarget]             = useState(null);
  const [sendResult, setSendResult]             = useState(null); // { sent: number }

  // Moderation tab
  const [modEventId, setModEventId]   = useState('');
  const [pending, setPending]         = useState([]);
  const [modLoading, setModLoading]   = useState(false);
  const [showRejectModal, setShowRejectModal]   = useState(false);
  const [rejectTarget, setRejectTarget]         = useState(null);
  const [rejectNote, setRejectNote]             = useState('');

  // Upload tab
  const [upEventId, setUpEventId]   = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('PRE_EVENT_MATERIAL');
  const [uploadVis, setUploadVis]   = useState('CONFIRMED_ONLY');
  const [versionNote, setVersionNote] = useState('');
  const [uploading, setUploading]   = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const showSuccessMsg = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showErrorMsg   = (msg) => { setError(msg);   setTimeout(() => setError(''), 5000); };

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const all = await getEvents(orgId);
      setAllEvents(all.filter(e => e.status !== 'ARCHIVED'));
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load events.');
    } finally {
      setEventsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const loadMaterials = useCallback(async (eventId) => {
    if (!eventId) return;
    setMatLoading(true);
    try {
      const data = await getEventMedia(orgId, eventId);
      setMaterials(data || []);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load materials.');
    } finally {
      setMatLoading(false);
    }
  }, [orgId]);

  const loadPending = useCallback(async (eventId) => {
    if (!eventId) return;
    setModLoading(true);
    try {
      const data = await getPendingMediaModeration(orgId, eventId);
      setPending(data || []);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load pending media.');
    } finally {
      setModLoading(false);
    }
  }, [orgId]);

  // ── Materials: delete ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await deleteMedia(orgId, matEventId, deleteTarget.id);
      showSuccessMsg('File deleted.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await loadMaterials(matEventId);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to delete file.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Materials: visibility change ──────────────────────────────────────────────
  const handleVisChange = async () => {
    if (!visTarget) return;
    setActionLoading(true);
    try {
      await updateMediaPrivacy(orgId, matEventId, visTarget.id, newVisibility);
      showSuccessMsg('Visibility updated.');
      setShowVisModal(false);
      setVisTarget(null);
      await loadMaterials(matEventId);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to update visibility.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Materials: send to registrants ───────────────────────────────────────────
  const handleSend = async () => {
    if (!sendTarget) return;
    setActionLoading(true);
    setSendResult(null);
    try {
      const result = await sendMaterialToRegistrants(orgId, matEventId, sendTarget.id, adminId, adminName || '');
      setSendResult(result);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to send material.');
      setShowSendModal(false);
      setSendTarget(null);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Moderation: approve ───────────────────────────────────────────────────────
  const handleApprove = async (media) => {
    setActionLoading(true);
    try {
      await moderateMedia(orgId, modEventId, media.id, true, adminId);
      showSuccessMsg(`"${media.file_name}" approved.`);
      await loadPending(modEventId);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to approve media.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Moderation: reject ────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectTarget || !rejectNote.trim()) return;
    setActionLoading(true);
    try {
      await moderateMedia(orgId, modEventId, rejectTarget.id, false, adminId, rejectNote);
      showSuccessMsg(`"${rejectTarget.file_name}" rejected.`);
      setShowRejectModal(false);
      setRejectTarget(null);
      setRejectNote('');
      await loadPending(modEventId);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to reject media.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!upEventId || !uploadFile) return;
    setUploading(true);
    try {
      await uploadEventMaterial(orgId, upEventId, uploadFile, uploadType, uploadVis, adminId, adminName || '', versionNote);
      showSuccessMsg('File uploaded successfully.');
      setUploadFile(null);
      setVersionNote('');
      // Reset file input
      const input = document.getElementById('upload-file-input');
      if (input) input.value = '';
    } catch (e) {
      showErrorMsg(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2>Content &amp; Media</h2>
        <p>Manage event materials, moderate member photos, and upload content.</p>
      </div>

      <TabBar activeTab={activeTab} onSelect={setActiveTab} />

      {error   && <div className="bb-alert bb-alert--error">{error}</div>}
      {success && <div className="bb-alert bb-alert--success">{success}</div>}

      {/* ── EVENT MATERIALS ── */}
      {activeTab === 'materials' && (
        <div>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector
              events={allEvents}
              value={matEventId}
              onChange={(id) => { setMatEventId(id); loadMaterials(id); }}
            />
          )}

          {matEventId && (
            matLoading ? <div className="bb-loading-spinner" /> :
            materials.length === 0 ? (
              <div className="bb-empty-state" style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>No materials uploaded for this event.</div>
            ) : (
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Type</th>
                    <th>Visibility</th>
                    <th>Uploaded By</th>
                    <th>Date</th>
                    <th>Last Sent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map(m => (
                    <tr key={m.id}>
                      <td>
                        <a href={m.file_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--org-primary, #0F766E)', textDecoration: 'none', fontWeight: 500 }}>
                          {m.file_name}
                        </a>
                      </td>
                      <td style={{ fontSize: 12 }}>{TYPE_LABELS[m.type] || m.type}</td>
                      <td>
                        <span style={{
                          background: m.visibility === 'ADMIN_ONLY' ? '#FEF3C7' : m.visibility === 'ALL_MEMBERS' ? '#D1FAE5' : '#EDE9FE',
                          color: m.visibility === 'ADMIN_ONLY' ? '#92400E' : m.visibility === 'ALL_MEMBERS' ? '#065F46' : '#5B21B6',
                          padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        }}>
                          {VISIBILITY_LABELS[m.visibility] || m.visibility}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>{m.uploaded_by_name || m.uploaded_by}</td>
                      <td style={{ fontSize: 13 }}>{fmtDateTime(m.uploaded_at)}</td>
                      <td style={{ fontSize: 12, color: m.last_dispatched_at ? '#065F46' : '#9CA3AF' }}>
                        {m.last_dispatched_at
                          ? <>{fmtDateTime(m.last_dispatched_at)}<br /><span style={{ color: '#6B7280' }}>by {m.last_dispatched_by_name || 'Admin'} · {m.dispatch_count}×</span></>
                          : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="bb-btn-outline-sm"
                            onClick={() => { setVisTarget(m); setNewVisibility(m.visibility || 'CONFIRMED_ONLY'); setShowVisModal(true); }}>
                            Visibility
                          </button>
                          <button
                            className="bb-btn-outline-sm"
                            style={{ background: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' }}
                            onClick={() => { setSendTarget(m); setSendResult(null); setShowSendModal(true); }}>
                            Send to Registrants
                          </button>
                          <button className="bb-btn-danger-sm"
                            onClick={() => { setDeleteTarget(m); setShowDeleteModal(true); }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      )}

      {/* ── PHOTO MODERATION ── */}
      {activeTab === 'moderation' && (
        <div>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector
              events={allEvents}
              value={modEventId}
              onChange={(id) => { setModEventId(id); loadPending(id); }}
            />
          )}

          {modEventId && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontFamily: 'var(--bb-font-heading)', fontSize: 15, fontWeight: 700, color: '#0F766E', paddingLeft: '3ch' }}>Pending Photos</h3>
                {!modLoading && (
                  <span style={{
                    background: pending.length > 0 ? '#DC2626' : '#6B7280',
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    padding: '2px 8px', borderRadius: 12,
                  }}>
                    {pending.length} pending
                  </span>
                )}
              </div>

              {modLoading ? <div className="bb-loading-spinner" /> :
              pending.length === 0 ? (
                <div className="bb-empty-state" style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>No photos pending moderation.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {pending.map(m => (
                    <div key={m.id} style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                      {m.file_url && (
                        <img
                          src={m.file_url}
                          alt={m.file_name}
                          style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, wordBreak: 'break-word' }}>{m.file_name}</div>
                        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
                          Uploaded by {m.uploaded_by_name || m.uploaded_by} · {fmtDateTime(m.uploaded_at)}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            className="bb-btn-sm"
                            style={{ background: '#059669', color: '#fff', flex: 1 }}
                            onClick={() => handleApprove(m)}
                            disabled={actionLoading}
                          >
                            Approve
                          </button>
                          <button
                            className="bb-btn-danger-sm"
                            style={{ flex: 1, padding: '6px 12px' }}
                            onClick={() => { setRejectTarget(m); setRejectNote(''); setShowRejectModal(true); }}
                            disabled={actionLoading}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── UPLOAD MATERIAL ── */}
      {activeTab === 'upload' && (
        <div style={{ maxWidth: 520 }}>
          <h3 style={{ marginTop: 0, fontFamily: 'var(--bb-font-heading)', fontSize: 15, fontWeight: 700, color: '#0F766E', paddingLeft: '3ch' }}>Upload Event Material</h3>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector events={allEvents} value={upEventId} onChange={setUpEventId} />
          )}

          {upEventId && (
            <form onSubmit={handleUpload}>
              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>File <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  id="upload-file-input"
                  type="file"
                  required
                  onChange={e => setUploadFile(e.target.files[0] || null)}
                  className="bb-input"
                  style={{ padding: '6px 10px' }}
                />
              </div>

              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label style={{ paddingLeft: '3ch' }}>Type</label>
                  <select className="bb-select" value={uploadType} onChange={e => setUploadType(e.target.value)}>
                    <option value="PRE_EVENT_MATERIAL">Pre-Event Material</option>
                    <option value="POST_EVENT_PHOTO">Post-Event Photo</option>
                  </select>
                </div>
                <div className="bb-form-group">
                  <label style={{ paddingLeft: '3ch' }}>Visibility</label>
                  <select className="bb-select" value={uploadVis} onChange={e => setUploadVis(e.target.value)}>
                    <option value="CONFIRMED_ONLY">Confirmed Only</option>
                    <option value="ALL_MEMBERS">All Members</option>
                    <option value="ADMIN_ONLY">Admin Only</option>
                  </select>
                </div>
              </div>

              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>Version Note</label>
                <input
                  className="bb-input"
                  value={versionNote}
                  onChange={e => setVersionNote(e.target.value)}
                  placeholder="e.g. Slide deck v2, updated speaker info"
                />
              </div>

              <button className="bb-btn-primary" type="submit" disabled={uploading || !uploadFile}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {showDeleteModal && deleteTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Delete File</h3>
            <p>Are you sure you want to delete <strong>"{deleteTarget.file_name}"</strong>? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>Cancel</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleDelete} disabled={actionLoading}>
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Visibility Change Modal ── */}
      {showVisModal && visTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Change Visibility</h3>
            <p>Set visibility for <strong>"{visTarget.file_name}"</strong>:</p>
            <div className="bb-form-group" style={{ marginTop: 12 }}>
              <select className="bb-select" value={newVisibility} onChange={e => setNewVisibility(e.target.value)}>
                <option value="CONFIRMED_ONLY">Confirmed Only</option>
                <option value="ALL_MEMBERS">All Members</option>
                <option value="ADMIN_ONLY">Admin Only</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowVisModal(false); setVisTarget(null); }}>Cancel</button>
              <button className="bb-btn-primary" onClick={handleVisChange} disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send to Registrants Modal ── */}
      {showSendModal && sendTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            {sendResult ? (
              <>
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
                  <h3 style={{ margin: '0 0 8px' }}>Material Sent</h3>
                  <p style={{ margin: '0 0 4px', color: '#374151' }}>
                    <strong>"{sendTarget.file_name}"</strong> was dispatched to{' '}
                    <strong>{sendResult.sent} registrant{sendResult.sent !== 1 ? 's' : ''}</strong>.
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
                    Members and volunteers who signed up for this event have received an in-portal notification with a link to the file.
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <button className="bb-btn-primary" onClick={() => { setShowSendModal(false); setSendTarget(null); setSendResult(null); loadMaterials(matEventId); }}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Send Material to Registrants</h3>
                <p>
                  Send <strong>"{sendTarget.file_name}"</strong> to all members and volunteers who registered for this event?
                </p>
                <p style={{ fontSize: 13, color: '#6B7280', margin: '8px 0 0' }}>
                  An in-portal notification with a download link will be sent to every registrant (confirmed, waitlisted, and attended), regardless of current membership status. Cancelled registrations are excluded.
                </p>
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                  <button className="bb-btn-outline-sm" onClick={() => { setShowSendModal(false); setSendTarget(null); }} disabled={actionLoading}>
                    Cancel
                  </button>
                  <button
                    className="bb-btn-primary"
                    style={{ background: '#1D4ED8' }}
                    onClick={handleSend}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Sending...' : 'Send Now'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {showRejectModal && rejectTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Reject Photo</h3>
            <p>Reject <strong>"{rejectTarget.file_name}"</strong>? Please provide a reason:</p>
            <textarea
              className="bb-textarea"
              rows={3}
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Reason for rejection..."
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowRejectModal(false); setRejectTarget(null); setRejectNote(''); }}>Cancel</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleReject} disabled={actionLoading || !rejectNote.trim()}>
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
